import { useState, useCallback } from "react";
import { mutate } from "swr";
import fetch from "cross-fetch";

interface ApiFormOptions<T> {
  url: string;
  method?: "POST" | "PUT" | "PATCH" | "DELETE";
  onSuccess?: (data: any) => void;
  onError?: (error: string) => void;
  mutateKeys?: string[];
  successMessage?: string;
  errorMessage?: string;
  transformData?: (data: T) => any;
}

interface ApiFormReturn<T> {
  submit: (data: T) => Promise<void>;
  isLoading: boolean;
  error: string | null;
  reset: () => void;
}

export function useApiForm<T = any>(options: ApiFormOptions<T>): ApiFormReturn<T> {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    url,
    method = "POST",
    onSuccess,
    onError,
    mutateKeys = [],
    successMessage,
    errorMessage,
    transformData,
  } = options;

  const reset = useCallback(() => {
    setError(null);
    setIsLoading(false);
  }, []);

  const submit = useCallback(
    async (data: T) => {
      setIsLoading(true);
      setError(null);

      try {
        const body = transformData ? transformData(data) : data;

        const response = await fetch(url, {
          method,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });

        const json = await response.json();

        if (json.status === "ok" || response.ok) {
          // Mutate SWR caches
          await Promise.all(mutateKeys.map((key) => mutate(key)));

          // Success message can be shown via toast or other UI feedback

          if (onSuccess) {
            onSuccess(json);
          }
        } else {
          const errorMsg = json.error || json.message || errorMessage || "An error occurred";
          setError(errorMsg);

          if (onError) {
            onError(errorMsg);
          } else {
            // Default error handling - matching existing pattern
            alert("Error: " + errorMsg);
          }
        }
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : "Network error";
        setError(errorMsg);

        if (onError) {
          onError(errorMsg);
        } else {
          alert("Error: " + errorMsg);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [url, method, onSuccess, onError, mutateKeys, errorMessage, transformData],
  );

  return {
    submit,
    isLoading,
    error,
    reset,
  };
}

// Helper hook for common form patterns with delayed cache invalidation
export function useApiFormWithDelayedMutate<T = any>(
  options: ApiFormOptions<T> & { delayedMutateKeys?: string[]; delay?: number },
): ApiFormReturn<T> {
  const { delayedMutateKeys = [], delay = 3000, ...baseOptions } = options;

  const handleSuccess = useCallback(
    (data: any) => {
      if (options.onSuccess) {
        options.onSuccess(data);
      }

      // Delayed mutations for certain keys
      if (delayedMutateKeys.length > 0) {
        setTimeout(() => {
          Promise.all(delayedMutateKeys.map((key) => mutate(key)));
        }, delay);
      }
    },
    [options, delayedMutateKeys, delay],
  );

  return useApiForm({
    ...baseOptions,
    onSuccess: handleSuccess,
  });
}
